import React from 'react';
import { Camera, CameraOff, Upload, X, AlertTriangle } from 'lucide-react';
import { ProfileMediaUploader } from '../ProfileMediaUploader';
import { useAuth } from '../../../lib/auth-context';

interface StepFourProps {
  formData: {
    photos: string[];
  };
  onFormDataChange: (data: any) => void;
  errors: string[];
}

export function StepFour({ formData, onFormDataChange, errors }: StepFourProps) {
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const MAX_PHOTOS = 5;
  const { user } = useAuth(); // Get current user to access userId

  const handleUploadComplete = (url: string) => {
    if (formData.photos?.length >= MAX_PHOTOS) {
      setUploadError(`Vous ne pouvez pas ajouter plus de ${MAX_PHOTOS} photos`);
      return;
    }

    onFormDataChange({
      ...formData,
      photos: [...(formData.photos || []), url]
    });
    
    setUploadError(null);
  };

  const handleDeletePhoto = (urlToDelete: string) => {
    onFormDataChange({
      ...formData,
      photos: (formData.photos || []).filter(url => url !== urlToDelete)
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <Camera className="w-5 h-5 text-primary-500" />
        </div>
        <div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
            Ajoutez des photos
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Ajoutez jusqu’à 5 photos pour vous présenter ! Un voyage, un loisir, un moment qui vous ressemble…
          </p>
        </div>
      </div>

      {uploadError && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{uploadError}</p>
        </div>
      )}

      {formData.photos && formData.photos.length > 0 ? (
        <div className="space-y-4">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {formData.photos.length} {formData.photos.length > 1 ? 'photos' : 'photo'} ajoutée{formData.photos.length > 1 ? 's' : ''}
            </p>
          </div>
          <ProfileMediaUploader 
            userId={user?.uid || ''}
            onUploadComplete={handleUploadComplete}
            onUploadError={setUploadError}
            existingPhotos={formData.photos}
            onDeletePhoto={handleDeletePhoto}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800/80 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
              <CameraOff className="w-6 h-6 text-gray-400" />
            </div>
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">
              Aucune photo
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Les photos aident à donner vie à votre profil et à vous connecter avec d'autres pvtistes.
            </p>
            <ProfileMediaUploader 
              userId={user?.uid || ''}
              onUploadComplete={handleUploadComplete}
              onUploadError={setUploadError}
            />
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-100 dark:border-amber-800/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
                  Conseils pour vos photos
                </h4>
                <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-1 list-disc ml-4">
                  <li>Utilisez des photos qui montrent clairement votre visage</li>
                  <li>Ajoutez des images de vos voyages ou activités préférées</li>
                  <li>Évitez les images floues ou de mauvaise qualité</li>
                  <li>Maximum 5 photos par profil</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}